"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldAlert, Flame, CircleCheck as CheckCircle2, ArrowRight, PhoneCall, Gauge } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
const safetyHighlights = [{
  id: 1,
  title: "Normas NFPA e EN",
  desc: "Sistemas em conformidade estrita com normas internacionais de segurança."
}, {
  id: 2,
  title: "Atomização de precisão",
  desc: "Gotículas calibradas para supressão imediata e arrefecimento rápido."
}, {
  id: 3,
  title: "Cobertura uniforme",
  desc: "Bicos Spraying Systems Co. com distribuição angular milimétrica."
}];
export default function HeroProtection() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="herofireprotection" data-nav="light" className="relative overflow-hidden bg-background py-16 md:py-24 border-b border-border">
      {/* Decorative background grid and glow */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,hsl(215_90%_45%/0.08),transparent_60%)]" />
      <div className="pointer-events-none absolute -top-40 right-0 h-96 w-96 rounded-full bg-secondary/5 blur-3xl" />

      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-8">
          
          {/* Left Column: Copy & Actions */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }} className="lg:col-span-7 space-y-6">
            <div className="inline-flex items-center gap-2">
              <Badge variant="outline" className="px-3 py-1 bg-accent text-accent-foreground border-border/80 font-medium">
                <ShieldAlert className="size-3.5 mr-1 text-destructive" />
                Segurança Industrial e Engenharia
              </Badge>
              <Badge variant="secondary" className="px-3 py-1 hidden sm:inline-flex">
                Spraying Systems Co.
              </Badge>
            </div>

            <div className="space-y-4">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground font-default">
                Proteção contra incêndios
              </h1>
              <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl leading-relaxed">Desde a proteção de cozinhas comerciais até à salvaguarda de ativos industriais, a Spraying Systems Co. oferece tecnologia comprovada de pulverização para supressão de incêndios, concebida para atuar nos momentos mais críticos. Com bicos certificados e apoio completo de aplicação, ajudamo-lo a manter-se preparado — independentemente do risco.</p>
            </div>

            <div className="flex flex-wrap items-center gap-4 pt-2">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-md">
                <Link to="/ContactsPage">
                  <span>Consulte-nos</span>
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="border-border hover:bg-accent hover:text-accent-foreground">
                <Link to="/TechnicalSolutionsPage">
                  <Flame className="size-4 text-destructive mr-1" />
                  <span>Soluções técnicas</span>
                </Link>
              </Button>
            </div>

            {/* Quick Safety Spec Strip */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-6 border-t border-border/80">
              {safetyHighlights.map((item, i) => <div key={item.id} data-index={i} className="space-y-1">
                  <div className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
                    <CheckCircle2 className="size-4 text-secondary shrink-0" />
                    <span>{item.title}</span>
                  </div>
                  <p className="text-xs text-muted-foreground leading-normal">
                    {item.desc}
                  </p>
                </div>)}
            </div>
          </motion.div>

          {/* Right Column: Hero Visual & Data Card */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          scale: 0.98
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.55,
          ease: "easeOut",
          delay: 0.1
        }} className="lg:col-span-5 relative">
            <div className="relative mx-auto max-w-md lg:max-w-none">
              {/* Main Visual Frame */}
              <div className="relative overflow-hidden rounded-2xl border border-border bg-card shadow-xl">
                <img data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/fire_protection_system_industrial.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/fire_protection_system_industrial.png-wvc-sizes" alt="Sistema industrial de pulverização para proteção contra incêndios e arrefecimento" loading="lazy" decoding="async" className="h-72 sm:h-88 w-full object-cover" src="https://eurocontrol.pt/wp-content/uploads/2026/09/Captura-de-ecra-2026-09-07-as-16.09.58.png" />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground/80 via-foreground/20 to-transparent" />
                
                {/* Overlay details */}
                <div className="absolute bottom-0 inset-x-0 p-5 text-white">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-secondary-foreground">
                        Engenharia Eurocontrol
                      </p>
                      <h3 className="text-base font-bold text-white">
                        Sistemas de Cortina de Água e Dilúvio
                      </h3>
                    </div>
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/80 backdrop-blur-xs border border-white/20">
                      <Gauge className="size-5 text-primary-foreground" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating Quick Response Card */}
              <Card className="absolute -bottom-6 -left-4 sm:-left-6 max-w-xs shadow-lg border-border bg-card p-4 rounded-xl">
                <CardContent className="p-0 flex items-start gap-3">
                  <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-accent text-accent-foreground">
                    <PhoneCall className="size-5" />
                  </div>
                  <div className="space-y-0.5">
                    <p className="text-xs font-semibold text-foreground">
                      Apoio Técnico Especializado
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Dimensionamento de bicos para resposta a riscos severos.
                    </p>
                  </div>
                </CardContent>
              </Card>

            </div>
          </motion.div>

        </div>
      </div>
    </section>;
}